import type { ModalType } from '../props';

export interface ModalStackItem<T extends ModalType = ModalType> {
  id: string;
  type: T;
  openedAt: number;
}

export interface ModalStackAction {
  type: string;
  payload?: unknown;
}

export const DEFAULT_MODAL_STACK_KEY = 'modalStack';

export const EMPTY_MODAL_STACK: ModalStackItem[] = [];

const getModalStackActionTypes = (key: string) => ({
  open: `${key}/openModal`,
  close: `${key}/closeModal`,
  replace: `${key}/replaceModal`,
  clear: `${key}/closeAllModals`,
});

export const createModalStackActions = (key = DEFAULT_MODAL_STACK_KEY) => {
  const types = getModalStackActionTypes(key);

  return {
    openModal: (payload: ModalStackItem): ModalStackAction => ({ type: types.open, payload }),
    closeModal: (payload: string): ModalStackAction => ({ type: types.close, payload }),
    replaceModal: (payload: ModalStackItem): ModalStackAction => ({ type: types.replace, payload }),
    closeAllModals: (): ModalStackAction => ({ type: types.clear }),
  };
};

export type ModalStackActions = ReturnType<typeof createModalStackActions>;

const closeItem = (state: ModalStackItem[], id: string) =>
  state.some((item) => item.id === id) ? state.filter((item) => item.id !== id) : state;

const replaceItem = (state: ModalStackItem[], next: ModalStackItem) =>
  state.some((item) => item.id === next.id)
    ? state.map((item) => (item.id === next.id ? { ...item, ...next } : item))
    : state;

export const createModalStackReducer = (key = DEFAULT_MODAL_STACK_KEY) => {
  const types = getModalStackActionTypes(key);

  return (state: ModalStackItem[] = EMPTY_MODAL_STACK, unknownAction: { type: string }): ModalStackItem[] => {
    const action = unknownAction as ModalStackAction;

    switch (action.type) {
      case types.open:
        return [...state, action.payload as ModalStackItem];

      case types.close:
        return closeItem(state, action.payload as string);

      case types.replace:
        return replaceItem(state, action.payload as ModalStackItem);

      case types.clear:
        return state.length ? EMPTY_MODAL_STACK : state;

      default:
        return state;
    }
  };
};

export const modalStackReducer = createModalStackReducer();
